// Roadmap 5.1 (A2-285) — rows dated in a period the client has already
// filed FOR THE SAME COUNTRY. Two cases (engine/filed_period_overlap.py):
// rows outside this run's period that belong to a filed return, and a run
// whose own dates overlap a filed return (filing it would declare them twice).
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { filedPeriodOverlap } from "../lib/api";

const rowsText = (n, t) => t("ui.calculate.filed_period.rows", { count: n, defaultValue: `${n} ${n === 1 ? "row" : "rows"}` });
const filedAs = (p, t) => (p.filing_reference ? t("ui.calculate.filed_period.filed_as", { ref: p.filing_reference, defaultValue: ` (filed as ${p.filing_reference})` }) : "");

// A2-602: a date in the screen's own language; the raw text if unreadable.
function formatDate(iso, lang) {
  const d = new Date(String(iso).slice(0, 10) + "T00:00:00");
  if (Number.isNaN(d.getTime())) return String(iso);
  try {
    return new Intl.DateTimeFormat(lang === "cn" ? "zh-CN" : (lang || undefined), { dateStyle: "medium" }).format(d);
  } catch {
    return String(iso);
  }
}

export const FiledPeriodNote = ({ calcId }) => {
  const { t, i18n } = useTranslation();
  const [data, setData] = useState(null);
  useEffect(() => {
    let off = false;
    if (calcId) filedPeriodOverlap(calcId).then((d) => { if (!off) setData(d); }).catch(() => {});
    return () => { off = true; };
  }, [calcId]);
  const periods = (data && data.filed_periods) || [];
  const outside = periods.filter((p) => p.kind !== "SAME_DATES");
  const same = periods.filter((p) => p.kind === "SAME_DATES");
  const lateHeld = (data && Array.isArray(data.late_rows_held)) ? data.late_rows_held : [];
  if (!periods.length && !lateHeld.length) return null;
  return (
    <div data-testid="filed-period-note" className="mb-4 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 p-3 text-xs text-amber-900 dark:text-amber-100 space-y-2">
      {same.length > 0 && (
        <div data-testid="filed-period-same-dates">
          <div className="font-semibold">{t("ui.calculate.filed_period.same_title", { defaultValue: "This run covers dates already filed" })}</div>
          <ul className="mt-1 list-disc ml-4">
            {same.map((p) => (
              <li key={`s-${p.calc_id}`}>
                {t("ui.calculate.filed_period.same_item", { countries: p.countries.join(", "), rows: rowsText(p.rows, t), start: p.period_start, end: p.period_end, filed: filedAs(p, t), defaultValue: `${p.countries.join(", ")}: ${rowsText(p.rows, t)} on this return fall in ${p.period_start} → ${p.period_end}${filedAs(p, t)}.` })}
              </li>
            ))}
          </ul>
          <div className="mt-1">
            {t("ui.calculate.filed_period.same_body", {
              defaultValue: "Filing this return as well would declare them twice. If it is meant to correct that return, run exactly that period instead: its return page then shows how that country wants it corrected.",
            })}
          </div>
        </div>
      )}
      {lateHeld.length > 0 && (
        <div data-testid="filed-period-late-held">
          <div className="font-semibold">{t("ui.calculate.filed_period.late_held_title", { count: lateHeld.length,
            defaultValue: `${lateHeld.length} ${lateHeld.length === 1 ? "row" : "rows"} dated in a filed period wait for an answer and are in no correction` })}</div>
          <ul className="mt-1 list-disc ml-4">
            {lateHeld.slice(0, 10).map((r, i) => (
              <li key={`l-${r.invoice}-${r.date}-${i}`}>
                {t("ui.calculate.filed_period.late_held_item", { invoice: r.invoice || "—", date: r.date ? formatDate(r.date, i18n && i18n.language) : "—",
                  reasons: (r.flags || []).map((f) => t(`flag_titles.${f}`, { defaultValue: f })).join(" · "),
                  defaultValue: `${r.invoice || "—"} (${r.date ? formatDate(r.date, i18n && i18n.language) : "—"}): ${(r.flags || []).map((f) => t(`flag_titles.${f}`, { defaultValue: f })).join(" · ")}` })}
              </li>
            ))}
          </ul>
          {lateHeld.length > 10 && (
            <div>{t("ui.calculate.filed_period.late_held_more", { n: lateHeld.length - 10, defaultValue: `and ${lateHeld.length - 10} more` })}</div>
          )}
          <div className="mt-1">
            {t("ui.calculate.filed_period.late_held_body", {
              defaultValue: "They would correct a return you have already filed, but each needs an answer first, and this calculation cannot ask for it. Fix what each one names — in your file (then upload it again) or in the client's settings — and calculate this period again: the corrections then appear on this return.",
            })}
          </div>
        </div>
      )}
      {outside.length > 0 && (
        <div data-testid="filed-period-outside">
          <div className="font-semibold">{t("ui.calculate.filed_period.outside_title", { defaultValue: "Rows dated in a period you have already filed" })}</div>
          <ul className="mt-1 list-disc ml-4">
            {outside.map((p) => (
              <li key={`o-${p.calc_id}`}>
                {t("ui.calculate.filed_period.outside_item", { countries: p.countries.join(", "), rows: rowsText(p.rows, t), start: p.period_start, end: p.period_end, filed: filedAs(p, t), defaultValue: `${p.countries.join(", ")}: ${rowsText(p.rows, t)} dated ${p.period_start} → ${p.period_end}${filedAs(p, t)}.` })}
              </li>
            ))}
          </ul>
          <div className="mt-1">
            {t("ui.calculate.filed_period.outside_body", {
              defaultValue: "They are not on this return. If they were left out of that return, it needs correcting: calculate exactly that period again, choosing this upload as well as the one you filed it from. Its return page then shows how that country wants it corrected.",
            })}
          </div>
        </div>
      )}
    </div>
  );
};
